import { IoIosArrowBack } from "react-icons/io";
import { IoIosArrowForward } from "react-icons/io";
import { useCarousel } from "./useCarousel";
import { usePreviewImages } from "./usePreviewImages";
import { useState, useEffect } from "react";
import { IMAGE_COMPRESSOR } from "../../utils/imageCompressor";
import { useRealData } from "./useRealData";
import "./vlp.css";

export default function VehicleListingsPage({ type }) {
    const { images: previewImages, loading: previewLoading } = usePreviewImages();
    const { images: realImages, loading: realLoading} = useRealData();
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isHovered, setIsHovered] = useState(false);

    // Update images based on heroData or previewImages
    useEffect(() => {
        if (type === "live" && realImages.length > 0) {
            setImages(realImages);
            setLoading(realLoading);
        } else if (type === "preview" && previewImages.length > 0) {
            setImages(previewImages);
            setLoading(previewLoading);
        }
    }, [type, previewImages, previewLoading, realImages, realLoading]);

    const {
        currentSlide,
        totalSlides,
        showLeftArrow,
        showRightArrow,
        nextSlide,
        prevSlide,
        goToSlide,
        handleImageLoad,
        getImageObjectFit,
        getContainerAspectRatio,
        isImageLoaded,
    } = useCarousel(images);

    return (
        <div className="fixed inset-0 flex items-center justify-center bg-gray-100 overflow-hidden">
            <div className="relative w-full h-full max-w-full max-h-full flex items-center justify-center">
                {/* Main Carousel Container */}
                <div 
                    className="vlp-carousel relative bg-gray-100 overflow-hidden" 
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                    style={{ 
                        aspectRatio: getContainerAspectRatio(),
                        width: 'calc(100dvw)',
                        maxWidth: '100%',
                        maxHeight: 'calc(100dvh)'
                    }}
                >
                    {/* Count Display - Top Right - Only show when images are loaded and on hover */}
                    {!loading && images.length > 0 && isHovered && (
                        <div className="vlp-counter md:!text-base !text-xs">
                            {currentSlide + 1}/{totalSlides}
                        </div>
                    )}

                    {/* Left Navigation Button - Only show when images are loaded */}
                    {!loading && images.length > 0 && showLeftArrow && (
                        <button
                            type="button"
                            onClick={prevSlide}
                            className="vlp-nav-btn vlp-nav-btn--left md:!w-9 md:!h-9 !w-8 !h-8"
                            aria-label="Previous slide"
                        >
                            <IoIosArrowBack className="vlp-nav-icon !w-[14px]" aria-hidden />
                        </button>
                    )}

                    {/* Center Content Area with Images */}
                    <div className="relative w-full h-full bg-white">
                        {loading ? (
                            <div className="absolute inset-0 shimmer rounded-lg"></div>
                        ) : (
                            images.map((imageUrl, index) => (
                                <div
                                    key={index}
                                    className={`absolute inset-0 transition-opacity duration-500 ${
                                        index === currentSlide ? 'opacity-100' : 'opacity-0'
                                    }`}
                                >
                                    {/* Circular Rotating Loader for each image */}
                                    {!isImageLoaded(index) && (
                                        <div className="absolute inset-0 flex items-center justify-center z-10 bg-white bg-opacity-50">
                                            <div className="vlp-image-loader border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                                        </div>
                                    )}
                                    <img
                                        src={IMAGE_COMPRESSOR({ url: imageUrl, resolution: '700x' })}
                                        alt={`Slide ${index + 1}`}
                                        className={`w-full h-full ${getImageObjectFit(index)}`}
                                        onLoad={(e) => handleImageLoad(index, e)}
                                        loading={index === 0 ? "eager" : "lazy"}
                                    />
                                </div>
                            ))
                        )}
                    </div>

                    {/* Right Navigation Button - Only show when images are loaded */}
                    {!loading && images.length > 0 && showRightArrow && (
                        <button
                            type="button"
                            onClick={nextSlide}
                            className="vlp-nav-btn vlp-nav-btn--right md:!w-9 md:!h-9 !w-8 !h-8"
                            aria-label="Next slide"
                        >
                            <IoIosArrowForward  className="vlp-nav-icon !w-[14px]" aria-hidden />
                        </button>
                    )}

                    {/* Pagination Dots - Only show when images are loaded and on hover */}
                    {!loading && images.length > 0 && isHovered && (() => {
                        const maxDots = 5;
                        const middleDotIndex = 2;
                        let startIndex = 0;

                        if (totalSlides <= maxDots) {
                            startIndex = 0;
                        } else if (currentSlide < 2) {
                            startIndex = 0;
                        } else if (currentSlide > totalSlides - 3) {
                            startIndex = totalSlides - maxDots;
                        } else {
                            startIndex = 0;
                        }

                        // was: middle band used startIndex = currentSlide - 2 (sliding window)

                        const inMiddleBand =
                            totalSlides > maxDots &&
                            currentSlide >= 2 &&
                            currentSlide <= totalSlides - 3;

                        const dotsToShow = Math.min(totalSlides, maxDots);

                        return (
                            <div className="vlp-dots">
                                {Array.from({ length: dotsToShow }).map((_, i) => {
                                    const dotIndex = startIndex + i;
                                    const isActive = inMiddleBand
                                        ? i === middleDotIndex
                                        : dotIndex === currentSlide;

                                    return (
                                        <button
                                            key={i}
                                            type="button"
                                            onClick={() => goToSlide(dotIndex)}
                                            className={`vlp-dot${isActive ? " vlp-dot--active" : ""}`}
                                            aria-label={`Go to slide ${dotIndex + 1}`}
                                        />
                                    );
                                })}
                            </div>
                        );
                    })()}
                </div>
            </div>
        </div>
    );
}